import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Shield, AlertTriangle, CheckCircle2, Clock, Users, Heart, Pill, FileText,
  Activity, ClipboardList, Building2, ChevronDown, Star, BookOpen,
  CalendarCheck, UserCheck, Download, Plus, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function MetricCard({ value, label, icon: Icon, alert, sub }) {
  return (
    <Card className={`glass-card ${alert ? 'border-red-500/30' : 'border-border'}`}>
      <CardContent className="p-3 flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${alert ? 'bg-red-500/10' : 'bg-[#00A6A0]/10'}`}>
          <Icon className={`w-4 h-4 ${alert ? 'text-red-400' : 'text-[#00A6A0]'}`} />
        </div>
        <div>
          <p className={`text-xl font-bold ${alert ? 'text-red-400' : 'text-foreground'}`}>{value}</p>
          <p className="text-[10px] text-muted-foreground leading-tight">{label}</p>
          {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function Section({ title, icon: Icon, children, defaultOpen = false, badge }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 p-3 bg-muted/30 hover:bg-muted/50 transition-colors text-left">
        <Icon className="w-4 h-4 text-[#00A6A0]" />
        <span className="text-sm font-medium text-foreground flex-1">{title}</span>
        {badge && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-400">{badge}</span>}
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && <div className="p-4 space-y-3">{children}</div>}
    </div>
  );
}

export default function CQCReadiness() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('dashboard');
  const [seedingPolicies, setSeedingPolicies] = useState(false);
  const [supervisionDialog, setSupervisionDialog] = useState(false);
  const [svForm, setSvForm] = useState({ caregiver_name: '', date: '', type: 'one_to_one', notes: '' });
  const [meetingDialog, setMeetingDialog] = useState(false);
  const [mtForm, setMtForm] = useState({ title: 'Governance Meeting', date: '', decisions: '', actions: '' });
  const [auditDialog, setAuditDialog] = useState(false);
  const [auForm, setAuForm] = useState({ title: '', audit_type: 'general', findings: '', outcome: 'satisfactory' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try { const { data: d } = await axios.get(`${API}/api/cqc/inspection-readiness`, { withCredentials: true }); setData(d); } catch { /* ignore */ }
  };

  const seedPolicies = async () => {
    setSeedingPolicies(true);
    try {
      const { data: d } = await axios.post(`${API}/api/cqc/seed-policies`, {}, { withCredentials: true });
      toast.success(`${d.message} - review and customise in DSPT Dashboard > Policies`);
      fetchData();
    } catch { toast.error('Failed'); }
    finally { setSeedingPolicies(false); }
  };

  const saveSupervision = async () => {
    try {
      await axios.post(`${API}/api/cqc/supervision`, svForm, { withCredentials: true });
      toast.success('Supervision recorded'); setSupervisionDialog(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const saveMeeting = async () => {
    try {
      await axios.post(`${API}/api/cqc/governance-meetings`, { ...mtForm, decisions: mtForm.decisions ? [mtForm.decisions] : [], actions: mtForm.actions ? [mtForm.actions] : [] }, { withCredentials: true });
      toast.success('Meeting recorded'); setMeetingDialog(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const saveAudit = async () => {
    if (!auForm.title) return toast.error('Title required');
    try {
      await axios.post(`${API}/api/cqc/quality-audits`, { ...auForm, findings: auForm.findings ? [auForm.findings] : [] }, { withCredentials: true });
      toast.success('Audit recorded'); setAuditDialog(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (!data) return <DashboardLayout><div className="flex justify-center py-20"><div className="spinner" /></div></DashboardLayout>;

  const v = data.visits || {};
  const actions = data.outstanding_actions || [];

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="cqc-readiness-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-heading font-bold text-foreground flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#00A6A0]" />CQC Inspection Readiness
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">Real-time compliance metrics mapped to CQC Fundamental Standards (Regs 9-20)</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="border-border text-xs" onClick={seedPolicies} disabled={seedingPolicies} data-testid="seed-policies-btn">
              <FileText className="w-3 h-3 mr-1" />{seedingPolicies ? 'Creating...' : 'Seed DSPT Policies'}
            </Button>
          </div>
        </div>

        {/* Outstanding Actions */}
        {actions.length > 0 && (
          <Card className="border-amber-500/30 bg-amber-500/5" data-testid="outstanding-actions">
            <CardContent className="p-4">
              <p className="text-sm font-medium text-foreground mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" />Outstanding Actions ({actions.length})</p>
              <div className="space-y-1">
                {actions.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className={`w-1.5 h-1.5 rounded-full ${a.priority === 'high' ? 'bg-red-500' : a.priority === 'medium' ? 'bg-amber-500' : 'bg-blue-400'}`} />
                    <span className="text-muted-foreground w-24">{a.area}</span>
                    <span className="text-foreground">{a.action}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex gap-1 p-1 bg-muted rounded-lg overflow-x-auto">
          {[{ id: 'dashboard', label: 'Dashboard' }, { id: 'regulations', label: 'CQC Regulations' }, { id: 'governance', label: 'Governance' }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`whitespace-nowrap flex-1 py-2 px-3 rounded-md text-xs font-medium transition-colors ${tab === t.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid={`cqc-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* DASHBOARD TAB */}
        {tab === 'dashboard' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard value={`${v.completion_rate || 0}%`} label="Visit Completion" icon={CheckCircle2} />
              <MetricCard value={v.missed || 0} label="Missed Visits" icon={AlertTriangle} alert={v.missed > 0} sub={`${v.late || 0} late`} />
              <MetricCard value={data.medication?.errors || 0} label="Medication Errors" icon={Pill} alert={data.medication?.errors > 0} sub={`${data.medication?.error_rate || 0}% error rate`} />
              <MetricCard value={data.incidents?.open || 0} label="Open Incidents" icon={AlertTriangle} alert={data.incidents?.open > 0} sub={`${data.incidents?.total_period || 0} this period`} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard value={data.safeguarding?.open || 0} label="Open Safeguarding" icon={Shield} alert={data.safeguarding?.open > 0} />
              <MetricCard value={data.complaints?.open || 0} label="Open Complaints" icon={FileText} alert={data.complaints?.overdue > 0} sub={data.complaints?.overdue > 0 ? `${data.complaints.overdue} overdue` : ''} />
              <MetricCard value={data.care_plans?.overdue_reviews || 0} label="Overdue Reviews" icon={ClipboardList} alert={data.care_plans?.overdue_reviews > 0} sub={`${data.care_plans?.active || 0} active plans`} />
              <MetricCard value={data.expired_documents || 0} label="Expired Documents" icon={Clock} alert={data.expired_documents > 0} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard value={`${data.training?.compliance_rate || 0}%`} label="Training Compliance" icon={BookOpen} alert={data.training?.compliance_rate < 95} />
              <MetricCard value={data.competency?.recent_90d || 0} label="Competencies (90d)" icon={UserCheck} sub={`${data.competency?.total || 0} total`} />
              <MetricCard value={data.supervision?.recent_90d || 0} label="Supervisions (90d)" icon={Eye} sub={`${data.supervision?.total || 0} total`} />
              <MetricCard value={`${data.dbs_checks?.staff_with_dbs || 0}/${data.dbs_checks?.total_caregivers || 0}`} label="DBS Verified" icon={Shield} alert={data.dbs_checks?.expired > 0} sub={data.dbs_checks?.expired > 0 ? `${data.dbs_checks.expired} expired` : ''} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard value={data.feedback?.compliments || 0} label="Compliments" icon={Star} sub={`${data.feedback?.client || 0} client, ${data.feedback?.family || 0} family`} />
              <MetricCard value={data.quality_audits?.recent_90d || 0} label="Quality Audits (90d)" icon={ClipboardList} alert={data.quality_audits?.recent_90d === 0} />
              <MetricCard value={data.governance_meetings?.recent_90d || 0} label="Governance Mtgs (90d)" icon={CalendarCheck} alert={data.governance_meetings?.recent_90d === 0} />
              <MetricCard value={data.policies_needing_review || 0} label="Policies Due Review" icon={FileText} alert={data.policies_needing_review > 0} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard value={data.consent?.active || 0} label="Active Consents" icon={Shield} />
              <MetricCard value={data.risk_assessments?.active || 0} label="Risk Assessments" icon={Activity} sub={`${data.risk_assessments?.high_risk || 0} high risk`} />
              <MetricCard value={data.duty_of_candour?.total || 0} label="Duty of Candour" icon={Heart} />
              <MetricCard value={data.continuous_improvement?.lessons_learned || 0} label="Lessons Learned" icon={BookOpen} />
            </div>
            <Card className="glass-card border-border">
              <CardContent className="p-3 text-center">
                <p className="text-[10px] text-muted-foreground">{data.audit_trail?.total_events?.toLocaleString() || 0} immutable audit events &bull; All metrics traceable to source records &bull; No artificial scores</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* CQC REGULATIONS TAB */}
        {tab === 'regulations' && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">CQC Fundamental Standards mapped to system functionality</p>
            {(data.regulations || []).map(reg => (
              <Section key={reg.reg} title={`Reg ${reg.reg}: ${reg.title}`} icon={Shield}>
                <p className="text-xs text-muted-foreground mb-2"><strong>Quality Statement:</strong> {reg.quality_statement}</p>
                <p className="text-xs text-muted-foreground mb-1"><strong>System Features:</strong></p>
                <div className="flex flex-wrap gap-1">
                  {(reg.system_features || []).map((f, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-[#00A6A0]/10 text-[#00A6A0]">{f}</span>
                  ))}
                </div>
              </Section>
            ))}
          </div>
        )}

        {/* GOVERNANCE TAB */}
        {tab === 'governance' && (
          <div className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" className="btn-primary" onClick={() => setSupervisionDialog(true)} data-testid="add-supervision-btn"><Plus className="w-3 h-3 mr-1" />Supervision</Button>
              <Button size="sm" className="btn-primary" onClick={() => setMeetingDialog(true)} data-testid="add-meeting-btn"><Plus className="w-3 h-3 mr-1" />Governance Meeting</Button>
              <Button size="sm" className="btn-primary" onClick={() => setAuditDialog(true)} data-testid="add-audit-btn"><Plus className="w-3 h-3 mr-1" />Quality Audit</Button>
            </div>
            <Section title="Supervision Records" icon={Eye} count={data.supervision?.total} defaultOpen>
              <p className="text-xs text-muted-foreground">{data.supervision?.recent_90d || 0} in last 90 days, {data.supervision?.total || 0} total</p>
            </Section>
            <Section title="Governance Meetings" icon={CalendarCheck} count={data.governance_meetings?.total}>
              <p className="text-xs text-muted-foreground">{data.governance_meetings?.recent_90d || 0} in last 90 days, {data.governance_meetings?.total || 0} total</p>
            </Section>
            <Section title="Quality Audits" icon={ClipboardList} count={data.quality_audits?.total}>
              <p className="text-xs text-muted-foreground">{data.quality_audits?.recent_90d || 0} in last 90 days, {data.quality_audits?.total || 0} total</p>
            </Section>
          </div>
        )}
      </div>

      {/* Supervision Dialog */}
      <Dialog open={supervisionDialog} onOpenChange={setSupervisionDialog}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle>Record Supervision</DialogTitle><DialogDescription>Log a staff supervision session for CQC Reg 18 evidence.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Input value={svForm.caregiver_name} onChange={e => setSvForm(f => ({ ...f, caregiver_name: e.target.value }))} placeholder="Staff member name" className="bg-muted border-border text-foreground" />
            <Input type="date" value={svForm.date} onChange={e => setSvForm(f => ({ ...f, date: e.target.value }))} className="bg-muted border-border text-foreground" />
            <select value={svForm.type} onChange={e => setSvForm(f => ({ ...f, type: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm">
              <option value="one_to_one">1:1 Supervision</option><option value="group">Group Supervision</option><option value="spot_check">Spot Check</option><option value="appraisal">Annual Appraisal</option>
            </select>
            <textarea value={svForm.notes} onChange={e => setSvForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes and outcomes" className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px]" />
            <Button className="w-full btn-primary" onClick={saveSupervision} data-testid="save-supervision-btn">Save Supervision</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Meeting Dialog */}
      <Dialog open={meetingDialog} onOpenChange={setMeetingDialog}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle>Record Governance Meeting</DialogTitle><DialogDescription>Log a governance meeting for CQC Reg 17 evidence.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Input value={mtForm.title} onChange={e => setMtForm(f => ({ ...f, title: e.target.value }))} placeholder="Meeting title" className="bg-muted border-border text-foreground" />
            <Input type="date" value={mtForm.date} onChange={e => setMtForm(f => ({ ...f, date: e.target.value }))} className="bg-muted border-border text-foreground" />
            <textarea value={mtForm.decisions} onChange={e => setMtForm(f => ({ ...f, decisions: e.target.value }))} placeholder="Key decisions" className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px]" />
            <textarea value={mtForm.actions} onChange={e => setMtForm(f => ({ ...f, actions: e.target.value }))} placeholder="Action items" className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px]" />
            <Button className="w-full btn-primary" onClick={saveMeeting} data-testid="save-meeting-btn">Save Meeting</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit Dialog */}
      <Dialog open={auditDialog} onOpenChange={setAuditDialog}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle>Record Quality Audit</DialogTitle><DialogDescription>Log a quality audit for CQC Reg 17 evidence.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Input value={auForm.title} onChange={e => setAuForm(f => ({ ...f, title: e.target.value }))} placeholder="Audit title" className="bg-muted border-border text-foreground" />
            <select value={auForm.audit_type} onChange={e => setAuForm(f => ({ ...f, audit_type: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm">
              <option value="general">General</option><option value="medication">Medication</option><option value="care_records">Care Records</option><option value="infection_control">Infection Control</option><option value="health_safety">Health & Safety</option>
            </select>
            <select value={auForm.outcome} onChange={e => setAuForm(f => ({ ...f, outcome: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm">
              <option value="satisfactory">Satisfactory</option><option value="minor_actions">Minor Actions Required</option><option value="significant_actions">Significant Actions Required</option><option value="unsatisfactory">Unsatisfactory</option>
            </select>
            <textarea value={auForm.findings} onChange={e => setAuForm(f => ({ ...f, findings: e.target.value }))} placeholder="Key findings" className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px]" />
            <Button className="w-full btn-primary" onClick={saveAudit} data-testid="save-audit-btn">Save Audit</Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
